/**
 * Feedback form — report a bug, request a feature, or suggest an improvement.
 *
 * Emails the IT team AND files a GitHub issue (server-side). Captures the page,
 * browser and (for bugs, opt-in) recent console logs automatically so the
 * reporter doesn't have to know any of it.
 */

import { useState } from 'react';
import { submitHelpFeedback } from '@/api/endpoints/help.js';
import { getCapturedLogs } from '@/utils/consoleCapture.js';
import { useToast } from '@/components/ui/useToast.js';

const TYPES = [
  { key: 'bug', icon: 'fa-bug', color: '#e11d48', t: 'Something is broken', d: 'Report a bug' },
  { key: 'feature', icon: 'fa-wand-magic-sparkles', color: '#7c3aed', t: 'I have an idea', d: 'Request a feature' },
  { key: 'improvement', icon: 'fa-thumbs-up', color: '#0891b2', t: 'Make it better', d: 'Suggest an improvement' }
];

export default function FeedbackForm({ sections, defaultSection, defaultType = 'bug', onDone }) {
  const toast = useToast();
  const [type, setType] = useState(defaultType);
  const [section, setSection] = useState(defaultSection || 'getting-started');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeLogs, setIncludeLogs] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.warning('Just a little more', 'Please add a short title and a description.');
      return;
    }
    setBusy(true);
    try {
      const res = await submitHelpFeedback({
        type,
        section,
        title: title.trim(),
        description: description.trim(),
        consoleLogs: type === 'bug' && includeLogs ? getCapturedLogs() : null,
        currentUrl: window.location.href,
        browserInfo: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          platform: navigator.platform
        }
      });
      const extra = res.issueCreated ? '' : res.message ? ` (${res.message})` : '';
      toast.success('Thank you — sent!', `Your reference is ${res.ticketId}.${extra}`, 8000);
      setTitle('');
      setDescription('');
      onDone?.();
    } catch (err) {
      toast.error('Could not send', err?.message || 'Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="help-form" onSubmit={submit}>
      <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 18px', lineHeight: 1.55 }}>
        Tell us what happened or what you'd like — there's no wrong way to do this. We read every message.
        You don't need to know any technical details; just describe it in your own words.
      </p>

      <div className="help-field">
        <label>What kind of message is this?</label>
        <div className="help-type-grid">
          {TYPES.map((tp) => (
            <button
              type="button"
              key={tp.key}
              className={`help-type-card${type === tp.key ? ' active' : ''}`}
              onClick={() => setType(tp.key)}
            >
              <i className={`fas ${tp.icon}`} style={{ color: tp.color }} aria-hidden="true" />
              <div className="t">{tp.t}</div>
              <div className="d">{tp.d}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="help-field">
        <label htmlFor="fb-section">Which part of Omni is this about?</label>
        <select id="fb-section" className="help-select" value={section} onChange={(e) => setSection(e.target.value)}>
          {(sections || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="help-field">
        <label htmlFor="fb-title">
          A short title <span className="hint">— one line, like a headline</span>
        </label>
        <input
          id="fb-title"
          className="help-input"
          value={title}
          maxLength={160}
          placeholder={type === 'bug' ? "e.g. The Save button doesn't do anything" : 'e.g. Add a way to print a user list'}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="help-field">
        <label htmlFor="fb-desc">
          Tell us more{' '}
          <span className="hint">
            {type === 'bug' ? '— what you clicked, what you expected, and what happened instead' : '— describe your idea'}
          </span>
        </label>
        <textarea
          id="fb-desc"
          className="help-textarea"
          value={description}
          placeholder={
            type === 'bug'
              ? 'I clicked … I expected … but instead …'
              : 'It would help me if I could …'
          }
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {type === 'bug' && (
        <div className="help-field">
          <label className="help-check">
            <input type="checkbox" checked={includeLogs} onChange={(e) => setIncludeLogs(e.target.checked)} />
            <span>
              Include technical logs <span className="hint">(recommended — helps us find the problem faster; no personal
              files are included)</span>
            </span>
          </label>
        </div>
      )}

      <div className="help-form-actions">
        <button type="submit" className="help-btn-primary" disabled={busy}>
          <i className={`fas ${busy ? 'fa-spinner help-spin' : 'fa-paper-plane'}`} aria-hidden="true" />
          {busy ? 'Sending…' : 'Send to IT'}
        </button>
        {onDone && (
          <button type="button" className="help-btn-ghost" onClick={onDone} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
